import * as Crypto from "expo-crypto";

import { withOneRetry } from "./retry-send";

/**
 * Sending a message once, even when the network disagrees.
 *
 * A send can commit on the server and still fail here — the request times out,
 * the connection drops, the app is suspended mid-flight — and from this side
 * those look exactly like a send that never landed. Retrying used to be unsafe
 * for that reason: measured against a real Postgres, **25 retries out of 25**
 * created a second message. Every send now carries a `clientMessageId`, and the
 * server stores the first one and returns that same message for any later
 * attempt carrying the same key, so one retry is free.
 *
 * This module is only the wiring: the key generator plus a real `sleep`. The
 * rule about *which* failures are worth retrying lives in `retry-send.ts`,
 * which imports nothing and is therefore testable.
 */

/** A new key for one message. Stable across that message's retries. */
export const newClientMessageId = (): string => Crypto.randomUUID();

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `attempt` with a stable key, retrying once if the failure was a transport
 * one. Anything else is rethrown untouched, immediately.
 */
export function sendWithRetry<T>(
  attempt: (clientMessageId: string) => Promise<T>,
  options: { retryDelayMs?: number } = {},
): Promise<T> {
  return withOneRetry(
    attempt,
    newClientMessageId(),
    sleep,
    options.retryDelayMs,
  );
}
